import { useState, useReducer, useEffect, useRef, useCallback } from "react";
import logger from "@lululemon/mwa-ab-testing/esm/logger";
import { useRouter } from "next/router";
import { trackEvent } from "../util/analytics";

/**
 * Create a Custom Timer to capture Virtual Turns in Blue Triangle.
 * The start, update, and end methods should be called with the same parameters.
 * For more info, see: https://help.bluetriangle.com/hc/en-us/articles/360039526094-Custom-Timer-Implementation
 */

export const bttStart = (path, pageName) => {
  if (!window.bttUT || !pageName) {
    return;
  }

  window.bttUT.start({
    pageName,
    key: path,
    txnName: window.bttUT.txnName
  });
};

export const bttEnd = (path, pageName) => {
  if (!window.bttUT) {
    return;
  }

  window.bttUT.end({
    pageName,
    key: path,
    txnName: window.bttUT.txnName
  });
};

const getInitialState = (flagNames) => {
  return flagNames.reduce((accum, name) => {
    // eslint-disable-next-line no-param-reassign
    accum[name] = false;
    return accum;
  }, {});
};

const flagsReducer = (state, key) => {
  if (key.type && key.type === "reset") {
    return key.payload;
  }

  if (state[key]) {
    return state;
  }

  if (key) {
    return {
      ...state,
      [key]: true
    };
  }

  return state;
};

// On first load, the page-load-complete event is fired for us
// by Adobe Launch, and we should fire the window-onload event
// instead. On subsequent page transitions we use page-load-complete

let isFirstLoad = true;

const stripQuery = (path) => {
  if (!path) {
    return undefined;
  }

  return path.split("?")[0];
};

// Hook to fire page load events on pages with data
// dependencies. Takes an array of keys indicating
// which data objects are loading, and returns an
// object with a boolean for each key indicating whether
// it has been tracked, as well as a callback to mark
// keys as tracked. Call the callback for a given key
// when that key's data has been loaded and added to
// window.digitalData.
//
// On initial mount, fires `page-load-begin` analytics
// event. Then, when all keys have been tracked, fires
// 'page-load-complete'.
//
// Example usage:
//
// const [flags, setFlag] = useTrackPageLoad([
//   "page"
// ]);
// useEffect(() => {
//   digitalData.setPageData({
//     pageID: "cart",
//   });
//   setFlag("page");
// }, []);

const useTrackPageLoad = (flagNames, { getBttId = () => {} } = {}) => {
  const { asPath, events } = useRouter();
  const nextPath = useRef(stripQuery(asPath));
  const [pageLoadTracked, setPageLoadTracked] = useState(false);
  const [flags, setFlag] = useReducer(flagsReducer, flagNames, getInitialState);
  const flagValues = Object.values(flags);

  const trackPageLoadBegin = useCallback(() => {
    const currentBttId = getBttId(nextPath.current);

    trackEvent("page-load-begin");
    // Trigger Blue Triangle start on virtual page turns
    if (!isFirstLoad) {
      bttStart(nextPath.current, currentBttId);
    }

    setFlag({
      type: "reset",
      payload: getInitialState(flagNames)
    });
    setPageLoadTracked(false);
  }, [getBttId, flagNames]);

  const trackPageLoadBeginRef = useRef(trackPageLoadBegin);

  useEffect(() => {
    const trackPageLoadBegin = trackPageLoadBeginRef.current;
    trackPageLoadBegin();
    const handleRouteChangeStart = (url) => {
      const newPath = stripQuery(url);

      if (newPath === nextPath.current) {
        return;
      }

      nextPath.current = newPath;
      trackPageLoadBegin();
    };

    events.on("routeChangeStart", handleRouteChangeStart);

    return () => {
      events.off("routeChangeStart", handleRouteChangeStart);
    };
  }, [events]);

  const trackPageLoadComplete = useCallback(() => {
    const pageBttId = getBttId(nextPath.current);

    if (!isFirstLoad) {
      // Trigger Blue Triangle end on virtual page turns
      bttEnd(nextPath.current, pageBttId);
      trackEvent("page-load-complete");
    } else {
      trackEvent("window-onload");
      isFirstLoad = false;
    }
  }, [getBttId]);

  useEffect(() => {
    if (!pageLoadTracked && flagValues.every(Boolean)) {
      setPageLoadTracked(true);
      trackPageLoadComplete();
    }
  }, [flagValues, pageLoadTracked, trackPageLoadComplete]);

  useEffect(() => {
    logger.log("intializing personalization tracker");
    // Removes all listeners from scope
    const removeTriggers = () => {
      logger.log("removing personalization tracker");
      // eslint-disable-next-line no-use-before-define
      document.removeEventListener("LAPEL_BATCH_REQUEST_COMPLETE", resolve);
    };

    // Completes track and removes triggers
    const resolve = () => {
      logger.log("resolving personalization tracker");
      removeTriggers();
    };

    logger.log("listening for personalization tracker");
    // Main listener for Lapel personalization library readiness
    document.addEventListener("LAPEL_BATCH_REQUEST_COMPLETE", resolve);

    // Remove triggers if component unmounts
    return removeTriggers;
  }, []);

  useEffect(() => {
    if (pageLoadTracked) {
      return () => {};
    }

    let currentPath = nextPath.current; // Store the current value of nextPath.current
    const warningTimer = setTimeout(() => {
      trackPageLoadComplete();
    }, 20000);

    return () => {
      clearTimeout(warningTimer);
      currentPath = nextPath.current; // Update currentPath value when the component unmounts
    };
  }, [pageLoadTracked, flags, trackPageLoadComplete]);

  return [flags, setFlag];
};

export default useTrackPageLoad;
